import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { STATUS_LABEL, STATUS_CLASS, ORIGEM_LABEL, brDateTime, tempoRestante, lerConferidas, marcarConferida } from './promocoesWhitelabelUtil';
import { ResumoInline } from './PromocaoDiffPanel';
import './Reclamacoes.css';
import './PromocoesWhitelabel.css';

/**
 * Lote de promoções de whitelabel: várias propostas (uma por tenant) ligadas por um batch_id.
 *
 * Cada linha abre, aqui mesmo, o resumo da proposta com o painel de diferenças em foco
 * (expandir e recolher, sem trocar de página). Abrir o resumo carregado marca a proposta como
 * conferida nesta sessão, e só conferida entra na aprovação em lote. A página de revisão por
 * proposta continua existindo (link "Abrir página"). A decisão é
 * parcial: a API aplica proposta a proposta, cada uma na sua transação. Um tenant com conflito
 * (md5 mudou, proposta expirada) falha sozinho e os outros seguem; o resultado de cada um fica
 * na tela.
 */

const RES_LABEL = { aplicada: 'Aplicada', conflito: 'Conflito', erro: 'Erro', ignorada: 'Ignorada' };

function Modal({ title, children, footer, onClose }) {
    return (
        <div className="vr-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
            <div className="vr-modal md">
                <div className="vr-modal-head">{title}</div>
                <div className="vr-modal-body">{children}</div>
                <div className="vr-modal-foot">{footer}</div>
            </div>
        </div>
    );
}

function PromocaoLote() {
    const { batchId } = useParams();
    const navigate = useNavigate();
    const [lote, setLote] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [decisao, setDecisao] = useState(null);
    const [conferidas, setConferidas] = useState(lerConferidas());
    const [sel, setSel] = useState({});
    const [abertas, setAbertas] = useState({});      // id da proposta -> true
    const [detalhes, setDetalhes] = useState({});    // id -> {estado:'carregando'|'ok'|'erro', p?, erro?}
    const [nota, setNota] = useState('');
    const [modal, setModal] = useState(null);       // {kind:'aprovar'|'desfazer'}
    const [ciente, setCiente] = useState(false);
    const [sending, setSending] = useState(false);
    const [resultado, setResultado] = useState(null); // {acao, resultados}
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const r = await apiService.whitelabelPromotionBatchGet(batchId);
            if (!r || !Array.isArray(r.propostas)) throw new Error('resposta inesperada do lote');
            setLote(r);
            setConferidas(lerConferidas());
        } catch (e) {
            setLoadError(e.response?.data?.detail || e.message || 'Erro ao carregar o lote');
        }
    }, [batchId]);

    useEffect(() => { load(); }, [load]);

    const carregar = useCallback(async (id, forcar = false) => {
        setDetalhes((d) => (d[id] && !forcar && d[id].estado !== 'erro' ? d : { ...d, [id]: { estado: 'carregando' } }));
        try {
            const r = await apiService.whitelabelPromotionGet(id);
            if (!r || !r.cabecalho || !Array.isArray(r.itens)) throw new Error('resposta inesperada da proposta');
            setDetalhes((d) => ({ ...d, [id]: { estado: 'ok', p: r } }));
            marcarConferida(id);
            setConferidas(lerConferidas());
        } catch (e) {
            setDetalhes((d) => ({ ...d, [id]: { estado: 'erro', erro: e.response?.data?.detail || e.message || 'Erro ao carregar a proposta' } }));
        }
    }, []);

    const alterna = (id) => {
        const abrir = !abertas[id];
        setAbertas((a) => ({ ...a, [id]: abrir }));
        if (abrir && !(detalhes[id] && detalhes[id].estado === 'ok')) carregar(id);
    };
    const expandirTodas = (abrir, props) => {
        setAbertas(Object.fromEntries(props.map((p) => [p.id, abrir])));
        if (abrir) props.forEach((p) => { if (!(detalhes[p.id] && detalhes[p.id].estado === 'ok')) carregar(p.id); });
    };
    useEffect(() => {
        (async () => {
            try { setDecisao(await apiService.whitelabelPromotionCanDecide()); } catch (e) {
                setDecisao({ pode: false, motivo: e.response?.data?.detail || 'Não foi possível conferir sua permissão.', relogin: e.response?.data?.relogin });
            }
        })();
    }, []);

    // Ao voltar da revisão, a aba reabre este componente: relê as conferidas.
    useEffect(() => {
        const onFocus = () => setConferidas(lerConferidas());
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    if (loadError) {
        return (
            <div className="vr-page"><div className="vr-list">
                <button className="vr-btn" onClick={() => navigate('/promocoes-whitelabel')}>‹ Voltar às propostas</button>
                <div className="vr-card" style={{ marginTop: 14 }}>{loadError}</div>
            </div></div>
        );
    }
    if (!lote) return <div className="vr-page"><div className="vr-list">Carregando lote…</div></div>;

    const props = lote.propostas;
    const foiConferida = (p) => !!conferidas[String(p.id)];
    const aprovavel = (p) => p.status === 'pendente' && foiConferida(p) && p.a_aplicar > 0;
    const desfazivel = (p) => p.status === 'aplicada';
    const marcadas = (fn) => props.filter((p) => sel[p.id] && fn(p));
    const paraAprovar = marcadas(aprovavel);
    const paraDesfazer = marcadas(desfazivel);
    const pendentes = props.filter((p) => p.status === 'pendente');
    const naoConferidas = pendentes.filter((p) => !foiConferida(p));
    const podeDecidir = decisao && decisao.pode;
    const aoVivo = paraAprovar.filter((p) => p.ativos > 0);

    const toggle = (p) => setSel((s) => ({ ...s, [p.id]: !s[p.id] }));
    const marcarConferidas = () => {
        const s = {};
        props.forEach((p) => { if (aprovavel(p)) s[p.id] = true; });
        setSel(s);
    };

    const decidir = async (acao) => {
        const lista = acao === 'approve' ? paraAprovar : paraDesfazer;
        setSending(true);
        try {
            const r = await apiService.whitelabelPromotionBatchDecide(acao, batchId, lista.map((p) => p.id), nota);
            if (!r || !Array.isArray(r.resultados)) throw new Error('resposta inesperada do lote');
            setResultado({ acao, resultados: r.resultados });
            const ok = r.resultados.filter((x) => x.resultado === 'aplicada').length;
            const falhas = r.resultados.filter((x) => x.resultado === 'conflito' || x.resultado === 'erro').length;
            notify(`${ok} ${acao === 'approve' ? 'aplicada' : 'desfeita'}${ok === 1 ? '' : 's'}${falhas ? `, ${falhas} com falha` : ''}`, falhas ? 'error' : 'success');
            setSel({});
            setDetalhes({});
            await load();
            Object.keys(abertas).filter((id) => abertas[id]).forEach((id) => carregar(Number(id), true));
        } catch (e) {
            const data = e.response?.data || {};
            const det = data.detail;
            setModal({
                kind: 'erro', relogin: !!data.relogin,
                text: typeof det === 'object' && det ? det.mensagem : det || e.message || 'Erro ao decidir o lote',
            });
        } finally {
            setSending(false);
        }
    };

    const reentrar = async () => {
        try { await apiService.logout(); } catch (e) { /* segue para o login de todo jeito */ }
        window.location.href = `/login?redirect=${encodeURIComponent(`/promocoes-whitelabel-lote/${batchId}`)}`;
    };

    const porId = Object.fromEntries(props.map((p) => [p.id, p]));

    return (
        <div className="vr-page">
            {toast && <div className={`vr-toast ${toast.type}`}>{toast.msg}</div>}
            <div className="vr-list">
                <button className="vr-btn" onClick={() => navigate('/promocoes-whitelabel')}>‹ Propostas</button>
                <h1 className="vr-title" style={{ marginTop: 12 }}>Lote de promoções · {props.length} tenant{props.length === 1 ? '' : 's'}</h1>
                <p className="vr-subtitle">
                    Cada tenant tem a sua proposta e é aprovado separadamente: se um falhar, os outros seguem. Abra o resumo de cada
                    tenant aqui mesmo para ler as diferenças; só proposta conferida entra na aprovação. Cada proposta vale 4 horas.
                </p>

                {pendentes.length > 0 && decisao && !podeDecidir && (
                    <div className="vr-readonly-banner wl-banner-warn">
                        Você pode revisar, mas não decidir: {decisao.motivo}
                        {decisao.relogin && <button className="vr-btn vr-btn-sm" style={{ marginLeft: 10 }} onClick={reentrar}>Entrar de novo</button>}
                    </div>
                )}
                {naoConferidas.length > 0 && (
                    <div className="vr-readonly-banner wl-banner-warn">
                        {naoConferidas.length} proposta{naoConferidas.length > 1 ? 's' : ''} ainda sem conferência nesta sessão: {naoConferidas.map((p) => p.subdomain).join(', ')}.
                    </div>
                )}

                <div className="wl-lote-tools">
                    <button className="vr-btn vr-btn-sm" onClick={() => expandirTodas(true, props)}>Expandir todas</button>
                    <button className="vr-btn vr-btn-sm" onClick={() => expandirTodas(false, props)}>Recolher todas</button>
                    <span className="vr-muted vr-small">Abrir o resumo de um tenant o marca como conferido.</span>
                </div>
                <table className="vr-table">
                    <thead><tr>
                        <th></th><th>Tenant</th><th>De → para</th><th className="vr-num">Configs</th>
                        <th>Vale na hora</th><th>Conferida</th><th>Situação</th><th></th>
                    </tr></thead>
                    <tbody>
                        {props.map((p) => {
                            const elegivel = aprovavel(p) || desfazivel(p);
                            const det = detalhes[p.id];
                            return (
                                <React.Fragment key={p.id}>
                                <tr className="wl-lote-row">
                                    <td>
                                        <button className="wl-caret-btn" aria-expanded={!!abertas[p.id]}
                                            aria-label={`${abertas[p.id] ? 'Recolher' : 'Expandir'} o resumo de ${p.subdomain}`}
                                            onClick={() => alterna(p.id)}>{abertas[p.id] ? '▾' : '▸'}</button>
                                        <input id={`wl-sel-${p.id}`} type="checkbox" checked={!!sel[p.id]} disabled={!elegivel}
                                            title={elegivel ? '' : 'Só propostas pendentes e conferidas, ou aplicadas'}
                                            onChange={() => toggle(p)} />
                                    </td>
                                    <td>
                                        <div>{p.subdomain} <span className="vr-muted vr-small">#{p.id}</span></div>
                                        {p.observacao && <div className="vr-muted vr-small">{p.observacao.slice(0, 90)}</div>}
                                        {p.status === 'pendente' && <div className="vr-muted vr-small">{tempoRestante(p.expires_at)}</div>}
                                    </td>
                                    <td>{ORIGEM_LABEL[p.origem] || p.origem} → {ORIGEM_LABEL[p.destino] || p.destino}</td>
                                    <td className="vr-num">{p.a_aplicar} de {p.itens}</td>
                                    <td>
                                        {p.ativos > 0
                                            ? <span className="vr-chip wl-chip-live" title="Edita um config ATIVO: vale para os vendedores assim que aprovar">{p.ativos} ativo{p.ativos > 1 ? 's' : ''}</span>
                                            : <span className="vr-muted vr-small">não</span>}
                                    </td>
                                    <td>{p.status === 'pendente'
                                        ? (foiConferida(p) ? <span className="vr-chip vr-chip-granted">sim</span> : <span className="vr-chip">não</span>)
                                        : <span className="vr-muted vr-small">—</span>}</td>
                                    <td><span className={`vr-chip ${STATUS_CLASS[p.status] || ''}`}>{STATUS_LABEL[p.status] || p.status}</span>
                                        {p.decided_by_email && <div className="vr-muted vr-small">{p.decided_by_email} · {brDateTime(p.decided_at)}</div>}</td>
                                    <td className="vr-num">
                                        <button className="vr-btn vr-btn-sm vr-btn-primary" onClick={() => alterna(p.id)}>
                                            {abertas[p.id] ? 'Recolher' : 'Resumo'}
                                        </button>{' '}
                                        <button className="vr-btn vr-btn-sm"
                                            onClick={() => navigate(`/promocoes-whitelabel/${p.id}/${p.status === 'pendente' ? 'itens' : 'resumo'}`)}>
                                            Abrir página ›
                                        </button>
                                    </td>
                                </tr>
                                {abertas[p.id] && (
                                    <tr className="wl-lote-detail">
                                        <td colSpan={8}>
                                            {(!det || det.estado === 'carregando') && <div className="vr-muted">Carregando o resumo de {p.subdomain}…</div>}
                                            {det && det.estado === 'erro' && (
                                                <div className="wl-motivo">{String(det.erro)} <button className="vr-btn vr-btn-sm" onClick={() => carregar(p.id, true)}>Tentar de novo</button></div>
                                            )}
                                            {det && det.estado === 'ok' && <ResumoInline p={det.p} />}
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {(pendentes.length > 0 || props.some(desfazivel)) && (
                    <div className="vr-card" style={{ marginTop: 14 }}>
                        <h3>Decidir as selecionadas</h3>
                        <textarea id="wl-lote-nota" className="vr-textarea" value={nota} onChange={(e) => setNota(e.target.value)}
                            placeholder="Observação opcional, registrada em cada proposta junto com quem decidiu." />
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                            <button className="vr-btn" onClick={marcarConferidas}>Selecionar as conferidas</button>
                            <button className="vr-btn vr-btn-success" disabled={!podeDecidir || sending || paraAprovar.length === 0}
                                onClick={() => { setCiente(false); setModal({ kind: 'aprovar' }); }}>
                                {sending ? 'Aplicando…' : `Aprovar selecionadas (${paraAprovar.length})`}
                            </button>
                            <button className="vr-btn vr-btn-danger-outline" disabled={!podeDecidir || sending || paraDesfazer.length === 0}
                                onClick={() => setModal({ kind: 'desfazer' })}>
                                {`Desfazer selecionadas (${paraDesfazer.length})`}
                            </button>
                        </div>
                    </div>
                )}

                {resultado && (
                    <div className="vr-card" style={{ marginTop: 14 }}>
                        <h3>Resultado {resultado.acao === 'approve' ? 'da aprovação' : 'do desfazer'}</h3>
                        {resultado.resultados.map((x) => (
                            <div key={x.promotion_id} className="vr-beh-row d-eq">
                                <span className="vr-beh-name">{x.subdomain || (porId[x.promotion_id] || {}).subdomain} #{x.promotion_id}</span>
                                <span className={`wl-res-${x.resultado}`}>{RES_LABEL[x.resultado] || x.resultado}</span>
                                <span className="vr-muted vr-small">
                                    {x.aplicados != null ? `${x.aplicados} config${x.aplicados === 1 ? '' : 's'}` : ''}
                                    {x.detalhe ? ` ${typeof x.detalhe === 'object' ? (x.detalhe.mensagem || JSON.stringify(x.detalhe)) : x.detalhe}` : ''}
                                </span>
                            </div>
                        ))}
                        <div className="vr-muted vr-small" style={{ marginTop: 8 }}>Quem falhou continua pendente: abra a proposta para ver o conflito ou peça uma nova.</div>
                    </div>
                )}
            </div>

            {modal?.kind === 'aprovar' && (
                <Modal title="Aplicar as propostas selecionadas?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-success" disabled={aoVivo.length > 0 && !ciente}
                            onClick={() => { setModal(null); decidir('approve'); }}>Aprovar {paraAprovar.length}</button></>}>
                    <p>Vão ser aplicadas {paraAprovar.length} proposta{paraAprovar.length > 1 ? 's' : ''}, cada uma na sua transação: {paraAprovar.map((p) => p.subdomain).join(', ')}.
                        Se algum tenant tiver mudado aqui depois da proposta, só ele é recusado e os outros seguem.</p>
                    {aoVivo.length > 0 && (
                        <div className="vr-mbox warn">
                            <h4>Vale na hora</h4>
                            <ul>{aoVivo.map((p) => <li key={p.id}>{p.subdomain} — {p.ativos} config{p.ativos > 1 ? 's' : ''} ativo{p.ativos > 1 ? 's' : ''}</li>)}</ul>
                            <label className="wl-check">
                                <input id="wl-lote-ciente" type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} />
                                Conferi as diferenças de cada tenant e sei que estes configs mudam a tela dos vendedores agora.
                            </label>
                        </div>
                    )}
                    <p className="vr-muted vr-small">Dá para desfazer depois, tenant a tenant, enquanto ninguém editar esses configs.</p>
                </Modal>
            )}
            {modal?.kind === 'desfazer' && (
                <Modal title="Desfazer as propostas selecionadas?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-danger-outline" onClick={() => { setModal(null); decidir('undo'); }}>Desfazer {paraDesfazer.length}</button></>}>
                    Em cada tenant ({paraDesfazer.map((p) => p.subdomain).join(', ')}), os configs editados voltam ao conteúdo de antes e os criados são apagados.
                    Um tenant cujo config foi alterado depois da aprovação, ou cujo config criado já foi ativado, não é desfeito e os outros seguem.
                </Modal>
            )}
            {modal?.kind === 'erro' && (
                <Modal title="Não foi possível decidir o lote" onClose={() => setModal(null)}
                    footer={<>{modal.relogin && <button className="vr-btn vr-btn-primary" onClick={reentrar}>Entrar de novo</button>}
                        <button className="vr-btn" onClick={() => setModal(null)}>Fechar</button></>}>
                    <p>{modal.text}</p>
                </Modal>
            )}
        </div>
    );
}

export default PromocaoLote;
